<template>
	<div class="reservations-experiences-container">
		<CardSlashed v-if="userStore?.bannersData && !userStore?.bannersData?.has_reservations" class="card-info-banner">
			<template #left>
				<div class="card-info-banner-title">
					<h2>{{ getString('card_info_title_reservation') }}</h2>
					<p>{{ getString('card_info_description_1_reservation') }}</p>
					<a target="_blank" href="https://qromo-help.freshdesk.com/support/solutions/articles/202000100746-prenotazioni" class="icon-link">
						{{ getString('card_info_cta_1_reservations') }}
						<div class="wrapper-icon">
							<img src="https://img.qromo.io/img/icon-arrow-link-banner-geen.svg" alt="console icon">
						</div>
					</a>
				</div>
			</template>
			<template #right>
				<div class="wrapper-img">
					<picture>
						<source type="image/webp" srcset="https://img.qromo.io/img/image-info-banner-reservation.webp">
						<img src="https://img.qromo.io/img/image-info-banner-reservation.png" alt="product image">
					</picture>
				</div>
			</template>
		</CardSlashed>
		<AppCard>
			<template #header>
				<h3>{{ getString('experiences') }}</h3>
				<div class="header-actions">
					<AppButtonIcon prevent-change color="var(--color-bg-primary-1)" icon-name="icon-plus" @click="addExperience" />
					<AppButton :text="getString('save')" @click="saveExperiences" />
				</div>
			</template>
			<template #body>
				<div class="experiences-list">
					<div class="experience-container" v-for="experience in experiences" :key="experience.experience_id">
						<ExperienceItem :experience-id="experience.experience_id" />
					</div>
					<div class="no-experiences-container" v-if="!experiences || experiences.length === 0">
						<div class="no-exp">
							<div class="no-exp-text">{{ getString('no_experience') }}</div>
							<div class="no-exp-subtitle">{{ getString('no_experience_explain') }}</div>
						</div>
					</div>
				</div>
			</template>
		</AppCard>
		<div class="sheets">
			<AppBottomSheet :forceWidth="bottomSheetWidth" :active="experiencesStore.editElementsId !== null" @toggling-popup="toggleBottomSheetElements">
				<div class="elements-container" v-if="experiencesStore.editElementsId !== null">
					<ExperienceEditElements :experience-id="experiencesStore.editElementsId" />
				</div>
			</AppBottomSheet>
			<AppBottomSheet :forceWidth="bottomSheetWidth" :active="experiencesStore.editScheduleId !== null" @toggling-popup="toggleBottomSheetSchedule">
				<div class="schedule-container" v-if="experiencesStore.editScheduleId !== null">
					<ExperienceSchedule :experience-id="experiencesStore.editScheduleId" />
				</div>
			</AppBottomSheet>
			<AppBottomSheet :forceWidth="bottomSheetWidth" :active="experiencesStore.editInfoId !== null" @toggling-popup="toggleBottomSheetInfo">
				<div class="info-container" v-if="experiencesStore.editInfoId !== null">
					<ModalHeader :title="getString('experience_info')" />
					<ExperienceInfo :experience-id="experiencesStore.editInfoId" />
				</div>
			</AppBottomSheet>
		</div>
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import {useLanguageStore, useSvgStore} from "@/shared/stores";
import {useExperiencesStore, useRoomsStore, useUserStore} from "@/console-new/stores";
import ExperienceItem from "@/console-new/components/plugins/reservations/ExperienceItem.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import ExperienceEditElements from "@/console-new/components/plugins/reservations/ExperienceEditElements.vue";
import AppList from "@/shared/components/AppList.vue";
import ExperienceInfo from "@/console-new/components/plugins/reservations/ExperienceInfo.vue";
import ExperienceSchedule from "@/console-new/components/plugins/reservations/ExperienceSchedule.vue";
import CardSlashed from "@/console-new/components/shared/CardSlashed.vue";

export default {
	setup() {
		const experiencesStore = useExperiencesStore();
		const userStore = useUserStore();
		const roomsStore = useRoomsStore();
		return {getString: useLanguageStore().getString, experiencesStore, userStore, roomsStore};
	},
    name: "ReservationsExperiences",
	components: {
		CardSlashed,
		ExperienceSchedule, AppList, ExperienceEditElements, ModalHeader, ExperienceItem, ExperienceInfo, AppCard, AppBottomSheet, AppButton, AppButtonIcon},
	mixins: [WindowWidth],
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		experiences() {
			return this.experiencesStore.businessExperiences ? this.experiencesStore.businessExperiences.filter(experience => !experience.deleted) : [];
		}
	},
	methods: {
		saveExperiences() {
			this.experiencesStore.saveBusinessExperiences();
		},
		closeAllExperiences() {
			this.experiencesStore.businessExperiences.forEach(experience => {
				experience.activeDropdown = false;
			});
		},
		addExperience() {
			this.closeAllExperiences();
			this.experiencesStore.addNewExperience();
		},
		toggleBottomSheetElements(open) {
			if(!open) {
				this.experiencesStore.editElementsId = null;
			}
		},
		toggleBottomSheetSchedule(open) {
			if(!open) {
				this.experiencesStore.editScheduleId = null;
			}
		},
		toggleBottomSheetInfo(open) {
			if(!open) {
				this.experiencesStore.editInfoId = null;
			}
		},
	},
	created() {
		this.experiencesStore.getBusinessExperiences();

		if (!this.userStore.bannersData?.has_reservations) {
			this.userStore.fetchBannersData('has_reservations');
		}
	},
	mounted() {
		this.roomsStore.getBusinessRooms();
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.experiencesStore.getBusinessExperiences();
				this.roomsStore.getBusinessRooms();
				if (!this.userStore.bannersData?.has_reservations) {
					this.userStore.fetchBannersData('has_reservations');
				}
			}
		}
	},
}
</script>

<style scoped lang="scss">
.reservations-experiences-container {
	max-width: 1400px;
	margin: 0 auto;

	&:deep(.card-body) {
		padding: 0 !important;
	}

	&:deep(.dropdown-content) {
		border-radius: 13px;
	}

	&:deep(.card-header) {
		.header-actions {
			display: flex;
			gap: 8px;
			align-items: center;
		}
	}

	.experiences-list {
		display: flex;
		flex-direction: column;
		gap: 10px;

		.experience-container {
			position: relative;

			&:after {
				content: '';
				position: absolute;
				bottom: -6px;
				left: 0;
				width: 100%;
				height: 1px;
				background-color: var(--color-border-alpha);
			}

			&:last-child {
				&:after {
					display: none;
				}
			}
		}

		.no-experiences-container {
			display: flex;
			justify-content: center;
			align-items: center;
			background-color: var(--color-bg);
			border-radius: 13px;
			padding: 20px;

			.no-exp {
				width: 100%;
				background-color: var(--color-bg);
				border-radius: 13px;
				border: 2px dashed $primary;
				padding: 20px;
				display: flex;
				align-items: center;
				justify-content: center;
				flex-direction: column;
				min-height: 130px;
				gap: 6px;

				.no-exp-text {
					font-size: 18px;
					color: $primary;
					font-weight: 700;
				}

				.no-exp-subtitle {
					font-size: 14px;
					color: $primary;
					font-weight: 400;
				}
			}

		}
	}

	.card-info-banner {
		margin-bottom: 40px;
		.card-info-banner-title {
			width: 100%;
			height: fit-content;
			display: flex;
			align-items: flex-start;
			justify-content: flex-start;
			flex-direction: column;
			color: var(--color-txt-white);
			max-width: 400px;
			text-align: left;
			.align-text {
				display:flex;
				flex-direction: column;
			}
			&.divider {
				padding-bottom: 20px;
				border-bottom: 1px solid rgba(255, 255, 255, 0.5);
				width: 100%;
				margin-bottom: 20px;
			}
			h2 {
				font-size: 22px;
				display: inline-block;
				color: var(--color-txt-white);
				@media (max-width: 550px) {
					font-size: 20px;
					line-height: normal;
				}
			}
			p {
				font-size: 16px;
				line-height: 22px;
				color: var(--color-txt-white);
				margin-top: 10px;
				@media (max-width: 550px) {
					font-size: 14px;
					line-height: 20px;
				}
				&.br {
					margin-top: 15px!important;
				}
			}
			.card-info-banner-button {
				margin-top: 20px;
			}
			.icon-link {
				min-height: 40px;
				display: flex;
				align-items: center;
				justify-content: flex-start;
				cursor: pointer;
				gap: 7px;
				margin-top: 8px;
				opacity: .9;
				transition: opacity .2s $transition;
				padding-right: 20px;
				font-size: 14px;
				line-height: 14px;
				font-weight: 400;
				color: var(--color-secondary);
				font-style: italic;
				width: calc(100% - 20px);
				&:after {
					display: none;
				}
				.wrapper-icon {
					margin-top: 2px;
					width: 12px;
					height: 12px;
					display: flex;
					align-items: center;
					justify-content: center;
					transition: transform .2s $transition;
					transform: translateX(0);
					img {
						width: 100%;
						height: 100%;
						max-height: 180px;
						object-fit: contain;
					}
				}
				&:hover {
					opacity: 1;
					.wrapper-icon {
						transform: translateX(2px);
					}
				}
			}
		}
		&:deep(.slashed-card-container) {
			padding: 15px 20px;
			background: linear-gradient(204deg, #90CE00 10%, rgb(47, 36, 209) 90%);
			height: 100%;
			gap: 40px;
			.slashed-left {
				width: calc(65% - 20px);
				justify-content: flex-start;
				padding: 0;
				overflow: unset;
				border-radius: 0;
				flex-direction: column;
				.card-info-banner-title {
					width: 100%;
					max-width: unset;
				}

				@media (max-width: 550px) {
					width: 100%;
					padding: 0;
					min-height: unset;
					.card-info-banner-title {
						width: 100%;
						max-width: unset;
						padding: 0;
					}
				}
			}
			.slashed-right {
				width: calc(35% - 20px);
				display: flex;
				align-items: center;
				justify-content: center;
				padding: 0;
				@media (max-width: 550px) {
					width: 100%;
					padding: 0;
				}
			}
			@media (max-width: 550px) {
				flex-direction: column-reverse;
				gap: 0;
			}
		}
		.wrapper-img {
			width: 100%;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			picture {
				img {
					width: 100%;
					height: 100%;
					max-height: 250px;
					object-fit: contain;
					@media (max-width: 550px) {
						max-height: 180px;
					}
				}
			}
		}
		.icon-link {
			min-height: 40px;
			display: flex;
			align-items: center;
			justify-content: flex-start;
			cursor: pointer;
			gap: 7px;
			margin-top: 10px;
			opacity: .9;
			transition: opacity .2s $transition;
			padding-right: 20px;
			p {
				font-size: 14px;
				line-height: 14px;
				font-weight: 400;
				color: var(--color-secondary);
				margin: 0;
				font-style: italic;
			}
			.wrapper-icon {
				margin-top: 2px;
				width: 12px;
				height: 12px;
				display: flex;
				align-items: center;
				justify-content: center;
				transition: transform .2s $transition;
				transform: translateX(0);
				img {
					width: 100%;
					height: 100%;
					object-fit: contain;
				}
			}
			&:hover {
				opacity: 1;
				.wrapper-icon {
					transform: translateX(2px);
				}
			}
		}
	}
}

</style>